/**
 * Verificação pública do ASO, da guia e do prontuário — o endereço do QR impresso nos três.
 *
 * Confirma que o papel saiu do sistema: quem (nome resumido e CPF mascarado), de que empresa, que
 * exame, quando, em que clínica e com que médico, e quais documentos foram emitidos. Não mostra
 * aptidão nem nada do prontuário — quem escaneia já tem o papel; a página só diz que ele é
 * verdadeiro.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Paper, Stack, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import verificacaoDeAtendimentoService, {
  type VerificacaoDeAtendimento,
} from '../../services/verificacaoDeAtendimentoService';

function dataBr(valor: string): string {
  // `selectedDate` chega como AAAA-MM-DD: sem fuso, para não voltar um dia.
  const [ano, mes, dia] = valor.slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

const Linha: React.FC<{ rotulo: string; valor: string | null }> = ({ rotulo, valor }) =>
  valor ? (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography>{valor}</Typography>
    </Box>
  ) : null;

export const VerificarDocumentoPage: React.FC = () => {
  const { codigo: codigoDaRota = '' } = useParams<{ codigo: string }>();
  const navigate = useNavigate();

  const [codigo, setCodigo] = useState(codigoDaRota);
  const [dados, setDados] = useState<VerificacaoDeAtendimento | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  const buscar = useCallback(async (alvo: string) => {
    if (!alvo.trim()) return;
    setErro(null);
    setBuscando(true);
    try {
      setDados(await verificacaoDeAtendimentoService.verificar(alvo.trim()));
    } catch (err: unknown) {
      setDados(null);
      setErro(err instanceof Error ? err.message : 'Nenhum documento com este código de verificação.');
    } finally {
      setBuscando(false);
    }
  }, []);

  useEffect(() => {
    setCodigo(codigoDaRota);
    void buscar(codigoDaRota);
  }, [codigoDaRota, buscar]);

  const emitidos = dados
    ? [dados.documentos.aso && 'ASO', dados.documentos.guia && 'Guia de encaminhamento', dados.documentos.prontuario && 'Prontuário'].filter(
        (d): d is string => Boolean(d)
      )
    : [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Paper sx={{ maxWidth: 680, mx: 'auto', p: { xs: 2.5, sm: 4 } }}>
        <Typography variant="h5" gutterBottom>
          Verificar ASO, guia ou prontuário
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Informe o código impresso junto ao QR code do documento.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            label="Código de verificação"
            placeholder="ABCD-2345-EFGH"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            size="small"
            fullWidth
            inputProps={{ style: { fontFamily: 'monospace' } }}
          />
          <Button
            variant="contained"
            disabled={buscando || !codigo.trim()}
            onClick={() => navigate(`/verificar-documento/${encodeURIComponent(codigo.trim())}`)}
          >
            Verificar
          </Button>
        </Stack>

        {erro && <Alert severity="warning">{erro}</Alert>}

        {dados && (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Documento autêntico: este atendimento foi registrado no sistema.
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
              <Chip label={dados.tipoDeExame} color="primary" />
              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {dados.codigo}
              </Typography>
            </Stack>

            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Linha
                rotulo="Trabalhador"
                valor={[dados.trabalhador.nome, dados.trabalhador.cpf && `CPF ${dados.trabalhador.cpf}`].filter(Boolean).join(' · ')}
              />
              <Linha rotulo="Empresa" valor={dados.empresa} />
              <Linha rotulo="Clínica" valor={dados.clinica} />
              <Linha rotulo="Data do exame" valor={dataBr(dados.dataDoExame)} />
              <Linha
                rotulo="Atendimento concluído em"
                valor={dados.concluidoEm ? new Date(dados.concluidoEm).toLocaleString('pt-BR') : null}
              />
              <Linha
                rotulo="Médico responsável"
                valor={dados.medico ? [dados.medico.nome, dados.medico.crm && `CRM ${dados.medico.crm}`].filter(Boolean).join(' · ') : null}
              />
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Documentos emitidos
            </Typography>
            {emitidos.length > 0 ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {emitidos.map((d) => (
                  <Chip key={d} size="small" variant="outlined" label={d} />
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum documento emitido ainda.
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 3 }}>
              Por sigilo médico, esta página não mostra a aptidão nem o conteúdo do prontuário.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default VerificarDocumentoPage;
